import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Wheel } from "react-custom-roulette";

export interface RouletteOption {
  id: string;
  name: string;
  weight: number;
  color: string;
  dailyLimit: number;
  currentCount: number;
}

export const defaultOptions: RouletteOption[] = [
  {
    id: "toppoki",
    name: "TOPPOKI+GOODIE",
    weight: 120,
    color: "#FF6B6B",
    dailyLimit: 120,
    currentCount: 0,
  },
  {
    id: "japchae",
    name: "JAPCHAE+GOODIE",
    weight: 192,
    color: "#4ECDC4",
    dailyLimit: 192,
    currentCount: 0,
  },
  {
    id: "sauce_kimchi",
    name: "SAUCE KIMCHI+GOODIE",
    weight: 24,
    color: "#96CEB4",
    dailyLimit: 24,
    currentCount: 0,
  },
  {
    id: "sauce_bbq",
    name: "SAUCE BBQ+GOODIE",
    weight: 24,
    color: "#DDA0DD",
    dailyLimit: 24,
    currentCount: 0,
  },
  {
    id: "twice_concert",
    name: "TWICE CONCERT TICKET+GOODIE",
    weight: 5,
    color: "#FF69B4",
    dailyLimit: 5,
    currentCount: 0,
  },
];

const RouletteWheel = () => {
  const navigate = useNavigate();
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [result, setResult] = useState<RouletteOption | null>(null);
  const [options, setOptions] = useState<RouletteOption[]>(defaultOptions);

  // localStorage에서 옵션 로드 (기존 설정 유지)
  useEffect(() => {
    const savedOptions = localStorage.getItem("rouletteOptions");
    if (savedOptions) {
      try {
        const parsedOptions = JSON.parse(savedOptions);
        setOptions(parsedOptions);
      } catch (error) {
        console.error("Error parsing saved options:", error);
        setOptions(defaultOptions);
        localStorage.setItem("rouletteOptions", JSON.stringify(defaultOptions));
      }
    } else {
      // 처음 방문시에만 기본값 설정
      setOptions(defaultOptions);
      localStorage.setItem("rouletteOptions", JSON.stringify(defaultOptions));
    }
  }, []);

  // localStorage에 옵션 저장
  const saveOptions = (newOptions: RouletteOption[]) => {
    setOptions(newOptions);
    localStorage.setItem("rouletteOptions", JSON.stringify(newOptions));
  };

  // 재고가 있는 옵션들만 필터링 (0개인 상품 제외)
  const availableOptions = options.filter(
    (option) => option.dailyLimit - option.currentCount > 0
  );

  // 라이브러리용 데이터 변환 (각 상품 하나씩, 갯수는 확률 계산에만 사용)
  const wheelData = availableOptions.map((option) => ({
    option: option.name, // 상품명만 표시
    style: {
      backgroundColor: option.color,
      textColor: "#ffffff",
    },
  }));

  const handleSpinClick = () => {
    if (!mustSpin && availableOptions.length > 0) {
      // 남은 갯수 기반 가중치 확률 계산
      const totalRemaining = availableOptions.reduce(
        (sum, option) => sum + (option.dailyLimit - option.currentCount),
        0
      );
      const randomNumber = Math.random() * totalRemaining;

      let cumulativeCount = 0;
      let selectedIndex = 0;

      for (let i = 0; i < availableOptions.length; i++) {
        const remainingCount =
          availableOptions[i].dailyLimit - availableOptions[i].currentCount;
        cumulativeCount += remainingCount;
        if (randomNumber <= cumulativeCount) {
          selectedIndex = i;
          break;
        }
      }

      setPrizeNumber(selectedIndex);
      setMustSpin(true);
    }
  };

  const handleStopSpinning = () => {
    setMustSpin(false);
    // availableOptions에서 직접 선택
    const selectedOption = availableOptions[prizeNumber];

    // 재고 업데이트
    const updatedOptions = options.map((opt) =>
      opt.id === selectedOption.id
        ? { ...opt, currentCount: opt.currentCount + 1 }
        : opt
    );
    saveOptions(updatedOptions);
    setResult(selectedOption);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 flex flex-col items-center justify-center p-2">
      <div className="w-full text-center">
        <div className="flex justify-center items-center mb-2">
          <h1
            className="text-2xl md:text-4xl font-bold text-gray-800 text-center"
            onClick={() => navigate("/roulette/settings")}
          >
            Jeu de Roulette
          </h1>
        </div>

        <style>{`
          .parent-container {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .parent-container > div {
            height: 90vmin !important;
            width: 90vmin !important;
            max-height: unset !important;
            max-width: unset !important;
          }
        `}</style>
        {/* Wheel */}
        <div className="relative mb-4 flex justify-center">
          <div className="w-[90vmin] h-[90vmin] parent-container">
            {" "}
            {availableOptions.length > 0 ? (
              <Wheel
                mustStartSpinning={mustSpin}
                prizeNumber={prizeNumber}
                data={wheelData}
                onStopSpinning={handleStopSpinning}
                backgroundColors={["#3e3e3e", "#df3428"]}
                textColors={["#ffffff"]}
                outerBorderColor="#eeeeee"
                outerBorderWidth={15}
                innerBorderColor="#30261a"
                innerBorderWidth={0}
                innerRadius={0}
                radiusLineColor="#eeeeee"
                radiusLineWidth={2}
                fontSize={10}
                textDistance={50}
              />
            ) : (
              <div className="text-2xl font-bold text-gray-600 p-8 text-center">
                Épuisé - Tous les prix ont été distribués
              </div>
            )}
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className="mb-8 p-6 bg-white rounded-lg shadow-lg border-2 border-gray-200 mx-4 text-center">
            <h2 className="text-2xl font-bold text-gray-700 mb-2">
              Résultat :
            </h2>
            <div
              className="text-4xl md:text-6xl font-bold mb-2"
              style={{ color: result.color }}
            >
              {result.name}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="space-y-4 px-4 flex justify-center">
          <button
            onClick={handleSpinClick}
            disabled={mustSpin || availableOptions.length === 0}
            className={`px-8 py-4 text-lg md:text-xl font-bold rounded-lg shadow-lg transition-all duration-200 ${
              mustSpin || availableOptions.length === 0
                ? "bg-gray-400 cursor-not-allowed text-gray-600"
                : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white transform hover:scale-105"
            }`}
          >
            {mustSpin ? "En cours..." : "Faire tourner la roulette !"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouletteWheel;
